import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Check, X, MapPin, Calendar, Users, CheckSquare, Square, ArrowUpDown, Trash2, FolderPlus, Loader2, GripVertical, Sparkles, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import PhotoFrame from '../components/PhotoFrame';
import PhotoActionsMenu from '../components/PhotoActionsMenu';
import PhotoInspectModal, { fmtTaken } from '../components/PhotoInspectModal';
import usePhotoOps from '../components/usePhotoOps';
import useDragReorder from '../components/useDragReorder';

const FILTERS = [{ v: 'pending', l: 'Pending' }, { v: 'approved', l: 'Approved' }, { v: 'rejected', l: 'Rejected' }, { v: '', l: 'All' }];
const SORTS = [
  { v: 'taken', l: 'Date & time taken' },
  { v: 'uploaded', l: 'Upload date' },
  { v: 'title', l: 'Title / file name' },
  { v: 'member', l: 'Member' },
  { v: 'status', l: 'Status' },
  { v: 'album', l: 'Album order' },
];
const STATUS_STYLE = { approved: { background: '#e6f4ea', color: '#2e7d32' }, pending: { background: '#fff4e5', color: '#8a5a00' }, rejected: { background: '#fdecea', color: '#8a1f11' } };

const takenKey = (p) => (p.taken_at || (p.date_taken ? `${p.date_taken}T00:00:00` : '') || (p.created_at || ''));
const sortValue = (p, key) => {
  switch (key) {
    case 'taken': return takenKey(p);
    case 'uploaded': return p.created_at || '';
    case 'title': return (p.title || '').toLowerCase();
    case 'member': return (p.member_name || '').toLowerCase();
    case 'status': return p.status || '';
    case 'album': return `${(p.album || '~').toLowerCase()}|${String(p.album_order ?? 9999).padStart(5, '0')}|${takenKey(p)}`;
    default: return '';
  }
};

export default function PhotoModerationTab() {
  const { adminListPhotos, adminBulkPhotos, adminPhotoReviewDigestNow, adminAiDescribePhoto, mediaUrl } = useAuth();
  const [progress, setProgress] = useState(null); // {done,total}
  const [filter, setFilter] = useState('pending');
  const [sortKey, setSortKey] = useState('taken');
  const [dir, setDir] = useState('desc');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState([]);
  const [inspectId, setInspectId] = useState(null);
  const [albumFilter, setAlbumFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    adminListPhotos(filter || undefined).then(setItems).catch(() => setErr('Could not load photos. Please try again.')).finally(() => setLoading(false));
  }, [adminListPhotos, filter]);
  useEffect(() => { load(); }, [load]);

  const onChanged = useCallback((updated, deletedId) => {
    if (deletedId) { setItems((it) => it.filter((p) => p.id !== deletedId)); setInspectId((i) => (i === deletedId ? null : i)); return; }
    if (updated && updated.id) setItems((it) => it.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    load();
  }, [load]);
  const ops = usePhotoOps(onChanged);

  const albumNames = useMemo(() => Array.from(new Set(items.map((p) => p.album).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [items]);
  const sorted = useMemo(() => {
    const arr = [...items].filter((p) => !albumFilter || (albumFilter === '__none' ? !p.album : p.album === albumFilter)).sort((a, b) => { const va = sortValue(a, sortKey); const vb = sortValue(b, sortKey); return va < vb ? -1 : va > vb ? 1 : 0; });
    return dir === 'desc' ? arr.reverse() : arr;
  }, [items, sortKey, dir, albumFilter]);

  const ids = sorted.map((p) => p.id);
  const selOrdered = ids.filter((id) => sel.includes(id)); // sorted order is retained for album sequencing
  const allSelected = ids.length > 0 && ids.every((id) => sel.includes(id));
  const toggleAll = () => setSel(allSelected ? [] : ids);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulk = async (action) => {
    if (!selOrdered.length) return;
    let note = '';
    if (action === 'reject') note = window.prompt('Optional reason (shown to the members):') || '';
    if (action === 'delete' && !window.confirm(`Delete ${selOrdered.length} photo(s) permanently?`)) return;
    setBusy(true);
    try {
      const r = await adminBulkPhotos({ ids: selOrdered, action, note });
      const who = r.members_notified ? ` One summary email sent to each of ${r.members_notified} member(s).` : '';
      toast.success(`${action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Deleted'} ${r.count} photo(s).${who}`);
      setSel([]); load();
    } catch { toast.error('Bulk action failed. Please try again.'); }
    setBusy(false);
  };

  // Drag-and-drop ordering inside an album (only meaningful in "Album order" sort, ascending).
  const albumDrag = sortKey === 'album' && dir === 'asc';
  const albumPhotos = (name) => sorted.filter((p) => (p.album || '') === name);
  const reorderAlbum = async (next, moved, group) => {
    if (!group) return;
    setItems((it) => it.map((p) => { const j = next.findIndex((n) => n.id === p.id); return j >= 0 ? { ...p, album_order: j } : p; }));
    try { await adminBulkPhotos({ ids: next.map((p) => p.id), action: 'album', album: group }); toast.success(`Order saved for “${group}”.`); }
    catch { toast.error('Could not save the order.'); load(); }
  };
  const dnd = useDragReorder({ onReorder: reorderAlbum });

  const bulkAi = async () => {
    if (!selOrdered.length) return;
    const overwrite = window.confirm(`Describe ${selOrdered.length} photo(s).\n\nOK = replace existing titles/descriptions with the suggested text.\nCancel = only fill in empty fields (tags are always merged).`);
    setBusy(true); let ok = 0; let failed = 0;
    for (let i = 0; i < selOrdered.length; i += 1) {
      setProgress({ done: i, total: selOrdered.length });
      try { const r = await adminAiDescribePhoto(selOrdered[i], overwrite); setItems((it) => it.map((p) => (p.id === r.photo.id ? { ...p, ...r.photo } : p))); ok += 1; } catch { failed += 1; }
    }
    setProgress(null); setBusy(false);
    toast[failed ? 'warning' : 'success'](`Described ${ok} photo(s)${failed ? `, ${failed} failed` : ''}.`);
  };

  const inspectIdx = sorted.findIndex((p) => p.id === inspectId);
  const inspect = inspectIdx >= 0 ? sorted[inspectIdx] : null;
  const pendingCount = items.filter((i) => i.status === 'pending').length;

  const bulkItems = [
    { label: `Approve ${selOrdered.length}`, icon: Check, onClick: () => bulk('approve'), testId: 'bulk-approve' },
    { label: `Reject ${selOrdered.length}`, icon: X, onClick: () => bulk('reject'), testId: 'bulk-reject' },
    { label: 'Albums…', icon: FolderPlus, onClick: () => ops.openAlbumFor(selOrdered), testId: 'bulk-album' },
    { label: `Description & tags for ${selOrdered.length}`, icon: Sparkles, onClick: bulkAi, testId: 'bulk-ai' },
    { label: `Delete ${selOrdered.length}`, icon: Trash2, danger: true, onClick: () => bulk('delete'), testId: 'bulk-delete' },
  ];

  return (
    <div data-testid="photo-moderation-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} style={{ color: 'var(--maroon)' }} />
          <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Photo Approvals</h2>
          {filter === 'pending' && pendingCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="pending-count">{pendingCount}</span>}
          {pendingCount > 0 && (
            <button
              data-testid="review-digest-now"
              title="Moderators automatically receive one summary email per batch of newly shared photos. Click to send the current summary immediately."
              onClick={async () => {
                try {
                  const r = await adminPhotoReviewDigestNow();
                  toast.success(r.pending ? `Summary of ${r.pending} pending photo(s) sent to ${r.sent} moderator(s).` : 'Nothing new to summarise — moderators have already been told about all pending photos.');
                } catch { toast.error('Could not send the summary.'); }
              }}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border bg-white"
              style={{ borderColor: 'rgba(0,0,0,0.2)', color: 'var(--navy)' }}
            >
              <Mail size={12} /> Email summary now
            </button>
          )}
        </div>
        <div className="flex rounded overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.15)' }}>
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => { setFilter(f.v); setSel([]); }} data-testid={`photo-filter-${f.v || 'all'}`} className="px-3 py-1.5 text-sm font-title"
              style={filter === f.v ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--ink)' }}>{f.l}</button>
          ))}
        </div>
      </div>

      {/* Toolbar: sort + selection + bulk actions */}
      <div className="flex items-center gap-2 flex-wrap mb-4 p-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.03)' }} data-testid="moderation-toolbar">
        <ArrowUpDown size={14} style={{ color: 'var(--olive)' }} />
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="text-sm px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="photo-sort-key">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <button onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))} className="text-sm px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="photo-sort-dir">{dir === 'asc' ? 'Oldest / A→Z first' : 'Newest / Z→A first'}</button>
        <select value={albumFilter} onChange={(e) => { setAlbumFilter(e.target.value); setSel([]); }} className="text-sm px-2 py-1.5 rounded border bg-white" style={{ borderColor: albumFilter ? 'var(--maroon)' : 'rgba(0,0,0,0.2)' }} data-testid="photo-album-filter">
          <option value="">All albums</option><option value="__none">Not in an album</option>{albumNames.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="h-6 w-px mx-1" style={{ background: 'rgba(0,0,0,0.15)' }} />
        <button onClick={toggleAll} className="inline-flex items-center gap-1 text-sm px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="photo-select-all">
          {allSelected ? <CheckSquare size={14} /> : <Square size={14} />} {allSelected ? 'Deselect all' : 'Select all'}
        </button>
        <span className="text-sm" style={{ color: 'var(--navy)' }} data-testid="photo-selection-count">{selOrdered.length} selected</span>
        {selOrdered.length > 0 && <PhotoActionsMenu items={bulkItems} label={busy ? 'Working…' : `Actions on ${selOrdered.length}`} testId="bulk-actions" align="left" buttonStyle={{ background: 'var(--maroon)', color: 'var(--cream)', borderColor: 'var(--maroon)' }} />}
        {busy && <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--navy)' }} data-testid="bulk-progress"><Loader2 size={14} className="animate-spin" />{progress ? ` Describing ${progress.done + 1} of ${progress.total}…` : ''}</span>}
        <span className="ml-auto text-xs inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}>{sorted.length} photo{sorted.length !== 1 ? 's' : ''} · click a photo to inspect{albumDrag && <> · <GripVertical size={11} /> drag within an album to reorder</>}</span>
      </div>

      {err && <p className="text-sm mb-3 px-3 py-2 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid="moderation-error">{err}</p>}

      {loading ? <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
      : sorted.length === 0 ? (
        <p className="text-sm py-8 text-center rounded-lg" style={{ color: 'var(--olive)', background: 'rgba(0,0,0,0.03)' }} data-testid="moderation-empty">
          {filter === 'pending' ? 'No photos awaiting approval. All caught up!' : 'No photos here.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sorted.map((p) => {
            const checked = sel.includes(p.id);
            return (
              <div key={p.id} className="bg-white rounded-lg overflow-hidden border flex flex-col" data-testid={`moderation-item-${p.id}`}
                style={{ borderColor: dnd.isOver(p.id) ? 'var(--gold)' : checked ? 'var(--navy)' : 'rgba(0,0,0,0.1)', borderWidth: checked || dnd.isOver(p.id) ? 2 : 1, opacity: dnd.isDragging(p.id) ? 0.4 : 1, cursor: albumDrag && p.album ? 'grab' : undefined }}
                {...(albumDrag && p.album ? dnd.itemProps(albumPhotos(p.album), p, albumPhotos(p.album).findIndex((x) => x.id === p.id), p.album) : {})}>
                <div className="relative">
                  <PhotoFrame src={mediaUrl(p.url)} display={p.display} alt={p.title} className="w-full h-48 cursor-zoom-in" style={{ background: (p.display && p.display.bg && p.display.bg !== 'transparent') ? p.display.bg : 'rgba(0,0,0,0.04)' }} onClick={() => setInspectId(p.id)} testId={`moderation-thumb-${p.id}`} />
                  <label className="absolute top-2 left-2 bg-white/90 rounded p-1 flex cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} className="w-4 h-4 cursor-pointer" data-testid={`photo-check-${p.id}`} />
                  </label>
                  <div className="absolute top-2 right-2"><PhotoActionsMenu compact items={ops.actionsFor(p)} testId={`card-actions-${p.id}`} /></div>
                  <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase" style={STATUS_STYLE[p.status] || { background: 'rgba(0,0,0,0.06)' }} data-testid={`moderation-status-${p.id}`}>{p.status}</span>
                </div>
                <div className="p-3 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-title text-sm truncate" style={{ color: 'var(--navy)' }}>{p.title || 'Untitled'}</h4>
                    {p.album && <span className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap" style={{ background: 'var(--sage)' }} data-testid={`moderation-album-${p.id}`}>{p.album}{typeof p.album_order === 'number' ? ` #${p.album_order + 1}` : ''}</span>}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--olive)' }}>by {p.member_name}</p>
                  <div className="flex flex-wrap gap-2 text-[11px] mt-1.5" style={{ color: 'var(--olive)' }}>
                    {(p.taken_at || p.date_taken) && <span className="flex items-center gap-0.5" data-testid={`moderation-taken-${p.id}`}><Calendar size={11} /> {fmtTaken(p)}</span>}
                    {p.place && <span className="flex items-center gap-0.5"><MapPin size={11} /> {p.place}</span>}
                    {p.people && <span className="flex items-center gap-0.5"><Users size={11} /> {p.people}</span>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {p.status !== 'approved' && <button onClick={() => ops.moderate(p, 'approve')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: '#2e7d32', color: 'white' }} data-testid={`approve-${p.id}`}><Check size={13} /> Approve</button>}
                    {p.status !== 'rejected' && <button onClick={() => ops.moderate(p, 'reject')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-title" style={{ background: 'white', color: '#8a1f11', border: '1px solid #8a1f11' }} data-testid={`reject-${p.id}`}><X size={13} /> Reject</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inspect && (
        <PhotoInspectModal photo={inspect} src={mediaUrl(inspect.url)} actions={ops.actionsFor(inspect)} onClose={() => setInspectId(null)}
          onPhotoChange={(r) => setItems((it) => it.map((p) => (p.id === r.id ? { ...p, ...r } : p)))}
          position={`${inspectIdx + 1} of ${sorted.length}`}
          onPrev={sorted.length > 1 ? () => setInspectId(sorted[(inspectIdx - 1 + sorted.length) % sorted.length].id) : null}
          onNext={sorted.length > 1 ? () => setInspectId(sorted[(inspectIdx + 1) % sorted.length].id) : null} />
      )}
      {ops.modals}
    </div>
  );
}
